import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createOrUpdateStore, fetchMyStore } from './storeSlice';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import axios from '../../axiosConfig';
import Shell from '../dashboard/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api';

export default function CreateStore(){
  const dispatch = useDispatch();
  const { getAxios } = useSecureAuth();
  const { current: store, status, error } = useSelector(s => s.store || { status: 'idle' });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [theme, setTheme] = useState('modern');
  const [keyInfo, setKeyInfo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    // Check if store already exists
    dispatch(fetchMyStore());
  }, [dispatch]);

  // If store already exists, show a message
  if (store && store._id) {
    return (
      <Shell>
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
          <h2>Store Already Created</h2>
          <p>You already have a store: <strong>{store.name}</strong></p>
          <p>Store URL: <a href={`/store/${store.slug}`} target="_blank" rel="noopener noreferrer">
            {window.location.origin}/store/{store.slug}
          </a></p>
          <Button onClick={() => window.location.href = '/overview'}>
            Go to Overview
          </Button>
        </div>
      </Shell>
    );
  }

  const generateWithAI = async () => {
    if (!name || !keyInfo) { setAiError('Provide store name and key info.'); return; }
    setGenerating(true); setAiError(null);
    try {
      const prompt = `Suggest a concise store description (<= 60 words) and a theme choice (one of light, dark, modern, classic) for a store named "${name}". Key info: ${keyInfo}. Output JSON: {"description":"...","theme":"..."}`;
      const res = await axios.post(`/ai`, { prompt });
      const text = res.data.result || '';
      // naive JSON parse attempt
      const match = text.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        if (parsed.description) setDescription(parsed.description);
        if (parsed.theme) setTheme(parsed.theme);
      } else {
        setAiError('Could not parse AI response.');
      }
    } catch (e) {
      setAiError('AI generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) { setAiError('Name and slug are required.'); return; }
    const axios = getAxios();
    await dispatch(createOrUpdateStore({ payload: { name: name.trim(), slug: slug.trim(), description, theme }, axiosInstance: axios }));
  };

  const themes = [
    { value: 'modern', label: 'Modern', description: 'Clean and contemporary design', color: '#3B82F6' },
    { value: 'light', label: 'Light', description: 'Bright and minimal aesthetic', color: '#F59E0B' },
    { value: 'dark', label: 'Dark', description: 'Sleek dark theme', color: '#1F2937' },
    { value: 'classic', label: 'Classic', description: 'Traditional and elegant', color: '#7C3AED' }
  ];

  return (
    <Shell>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <h1 style={{ 
            fontSize: 'var(--font-size-4xl)', 
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-gray-900)',
            margin: '0 0 var(--space-2) 0'
          }}>
            {name ? `Edit ${name}` : 'Create Your Store'}
          </h1>
          <p style={{ 
            fontSize: 'var(--font-size-lg)', 
            color: 'var(--color-gray-600)',
            margin: 0
          }}>
            Set up your store with a beautiful design and compelling description
          </p>
        </div>

        {/* Error Messages */}
        {(error || aiError) && (
          <Card style={{ 
            marginBottom: 'var(--space-6)', 
            background: 'var(--color-error)',
            color: 'white',
            border: 'none'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <span>{error || aiError}</span>
            </div>
          </Card>
        )}

        <Card>
          <Card.Header>
            <Card.Title>Store Information</Card.Title>
            <Card.Description>Basic details about your store</Card.Description>
          </Card.Header>

          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Input
                label="Store Name"
                placeholder="Enter your store name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                icon="🏬"
              />
              <Input
                label="Store URL Slug"
                placeholder="your-store-name"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
                icon="🔗"
                helperText="This will be your store's public URL"
              />
            </div>

            <Input
              label="Store Description"
              placeholder="Describe what your store sells and what makes it special..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              icon="📝"
              helperText="This will appear on your storefront"
            />

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-gray-700)',
                marginBottom: 'var(--space-3)'
              }}>
                Store Theme
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                gap: 'var(--space-3)' 
              }}>
                {themes.map(themeOption => (
                  <div
                    key={themeOption.value}
                    onClick={() => setTheme(themeOption.value)}
                    style={{
                      padding: 'var(--space-4)',
                      border: `2px solid ${theme === themeOption.value ? themeOption.color : 'var(--color-gray-200)'}`,
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                      backgroundColor: theme === themeOption.value ? `${themeOption.color}10` : 'var(--bg-primary)'
                    }}
                    onMouseEnter={(e) => {
                      if (theme !== themeOption.value) {
                        e.target.style.borderColor = 'var(--color-gray-300)';
                        e.target.style.backgroundColor = 'var(--color-gray-50)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (theme !== themeOption.value) {
                        e.target.style.borderColor = 'var(--color-gray-200)';
                        e.target.style.backgroundColor = 'var(--bg-primary)';
                      }
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 'var(--space-3)',
                      marginBottom: 'var(--space-2)'
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: 'var(--radius-full)',
                        backgroundColor: themeOption.color,
                        border: theme === themeOption.value ? '3px solid white' : 'none',
                        boxShadow: theme === themeOption.value ? '0 0 0 2px ' + themeOption.color : 'none'
                      }} />
                      <span style={{ 
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-gray-900)'
                      }}>
                        {themeOption.label}
                      </span>
                    </div>
                    <p style={{ 
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-gray-600)',
                      margin: 0
                    }}>
                      {themeOption.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ 
              padding: 'var(--space-4)', 
              background: 'var(--color-gray-50)', 
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-gray-200)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-3)'
              }}>
                <span style={{ fontSize: '20px' }}>✨</span>
                <h3 style={{ 
                  fontSize: 'var(--font-size-lg)', 
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-gray-900)',
                  margin: 0
                }}>
                  AI-Powered Store Setup
                </h3>
              </div>
              <p style={{ 
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-gray-600)',
                margin: '0 0 var(--space-4) 0'
              }}>
                Let AI help you create a compelling store description and choose the perfect theme based on your business details.
              </p>
              <Input
                label="Key Business Information"
                placeholder="e.g., Electronics store, Fashion boutique, Handmade crafts, etc."
                value={keyInfo}
                onChange={(e) => setKeyInfo(e.target.value)}
                icon="💡"
                helperText="Describe your business type, products, or target audience"
              />
              <Button
                type="button"
                variant="outline"
                onClick={generateWithAI}
                disabled={generating || !name.trim() || !keyInfo.trim()}
                loading={generating}
                style={{ marginTop: 'var(--space-3)' }}
              >
                {generating ? 'Generating with AI...' : 'Generate with AI'}
              </Button>
            </div>

            <Card.Footer>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={status === 'loading' || !name.trim() || !slug.trim()}
                  loading={status === 'loading'}
                >
                  {status === 'loading' ? 'Saving Store...' : 'Save Store'}
                </Button>
              </div>
            </Card.Footer>
          </form>
        </Card>
      </div>
    </Shell>
  );
}


